import type { AxiosInstance } from 'axios';
import type { TFunction } from 'i18next';
import { createInstance } from 'i18next';
import type { HttpBackendOptions } from 'i18next-http-backend';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { LocalizationLanguage } from '@gov-nx/core/app';
import type { Optional } from '@gov-nx/core/types';
import { isArray } from '@gov-nx/core/types';
import { PageCode } from '@gov-nx/module/page';
import { usePromise } from '@gov-nx/utils/common';
import type { TranslationResponse } from './name-space.types';
import { LocalizeNameSpaceTypes } from './name-space.types';

const instance = createInstance();

export async function initTranslations(
	initialLanguage: LocalizationLanguage,
	axios: AxiosInstance,
	cmsKeyCache: string,
	getTranslationsByKey: (key: string) => Optional<TranslationResponse>,
	updateTranslations: (translations: TranslationResponse, key: string) => void,
	shouldInitAutorizaceNamespace: boolean,
	isOffline?: boolean
): Promise<TFunction> {
	return instance
		.use(initReactI18next)
		.use(HttpBackend)
		.init<HttpBackendOptions>({
			compatibilityJSON: 'v3',
			backend: {
				loadPath: '/translations/{{ns}}-{{lng}}.json?v=' + cmsKeyCache,
				request: async (options, url, payload, callback) => {
					try {
						if (isOffline) {
							const data = getTranslationsByKey(url);

							return callback(null, {
								status: 200,
								data: data?.data ?? {},
							});
						}

						const data = await axios.get(url);

						updateTranslations(data.data, url);

						return callback(null, {
							status: 200,
							data: data.data?.data,
						});
					} catch (error) {
						callback(error, {
							status: 500,
							data: {},
						});
					}
				},
			},
			react: {
				useSuspense: true,
			},
			lng: initialLanguage,
			fallbackLng: LocalizationLanguage.Czech,
			ns: [
				LocalizeNameSpaceTypes.Global,
				LocalizeNameSpaceTypes.Auth,
				LocalizeNameSpaceTypes.Page,
				LocalizeNameSpaceTypes.Service,
				LocalizeNameSpaceTypes.Component,
				LocalizeNameSpaceTypes.Form,
				PageCode['drozd'],
				PageCode['profil-ROB'],
				PageCode['datove-schranky'],
				PageCode['nastaveni-upozorneni'],
				...(shouldInitAutorizaceNamespace ? [PageCode['autorizace']] : []),
			],
			defaultNS: LocalizeNameSpaceTypes.Global,
			debug: false,
			interpolation: {
				escapeValue: false,
			},
		});
}

export function getI18n() {
	return instance;
}

export function i18nLoadNamespaces(namespaces: string[]): Promise<void[]> {
	return new Promise((resolve) => {
		const promises = Promise.all(
			namespaces
				.filter((namespace) => !getI18n().hasLoadedNamespace(namespace))
				.map((namespace) => getI18n().loadNamespaces(namespace))
		);

		return resolve(promises);
	});
}

export const useLanguageNamespaceLoader = (namespace: string | string[]) =>
	usePromise(i18nLoadNamespaces(isArray(namespace) ? namespace : [namespace]));
